import { useEffect, useState } from "preact/hooks";

/** The Library's screens (#15): the browser and search, the Bookmarks, the Recents. */
export type LibrarySection = "browse" | "bookmarks" | "recents";

/** Hash routes: "#/" home, "#/c/<id>" a Character, "#/library[/bookmarks|/recents]" the Library, "#/account" the Account (#26). Hash routing keeps the PWA on one URL. */
export type Route = { page: "home" } | { page: "character"; id: string } | { page: "library"; section: LibrarySection } | { page: "account" };

export function parseRoute(hash: string): Route {
  const m = /^#\/c\/([^/]+)$/.exec(hash);
  if (m) return { page: "character", id: decodeURIComponent(m[1]!) };
  const l = /^#\/library(?:\/(bookmarks|recents))?$/.exec(hash);
  if (l) return { page: "library", section: (l[1] as LibrarySection | undefined) ?? "browse" };
  if (hash === "#/account") return { page: "account" };
  return { page: "home" };
}

export function routeHash(route: Route): string {
  if (route.page === "character") return `#/c/${encodeURIComponent(route.id)}`;
  if (route.page === "library") return route.section === "browse" ? "#/library" : `#/library/${route.section}`;
  if (route.page === "account") return "#/account";
  return "#/";
}

export function navigate(route: Route): void {
  location.hash = routeHash(route);
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseRoute(location.hash));
  useEffect(() => {
    const on = () => setRoute(parseRoute(location.hash));
    addEventListener("hashchange", on);
    return () => removeEventListener("hashchange", on);
  }, []);
  return route;
}
